import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, X, CheckCircle, Package,
  Store, Users, Upload, Plus, Trash2, Mail,
  Building2, Camera, AlertCircle,
  ChevronDown, Send, MessageSquare, MapPin, Phone,
} from "lucide-react";
import { upsertCommercePartner, uploadCommercePartnerLogo } from "../../../../redux/commercePartnersSlice";
import { upsertProduct, upsertProductMedia } from "../../../../redux/productSlice";
import { AppDispatch, RootState } from "../../../../redux/store";
import { electronicsCategories, electronicsSubcategories, electronicsProducts } from "../../../../UNIVERSAL/CATEGORIES/ElectronicsCategories";



// ── API endpoints ────────────────────────────────────────────────
const SEND_INDIVIDUAL_EMAIL_URL = "https://europe-west3-metarelm.cloudfunctions.net/sendAcquisitionEmail";
const SEND_BULK_EMAIL_URL       = "https://europe-west3-metarelm.cloudfunctions.net/sendBulkAcquisitionEmail";
const SEND_MARKETING_EMAIL_URL  = "https://europe-west3-metarelm.cloudfunctions.net/sendMarketingEmail";
const SEND_WA_MARKETING_URL     = "https://europe-west3-metarelm.cloudfunctions.net/whatsappMarketing";

// ── Category groups ──────────────────────────────────────────────
const CATEGORY_GROUPS = [
  { name: "Electronics", categories: electronicsCategories, subs: electronicsSubcategories, products: electronicsProducts },
];

// ── Wait for Google Maps (exact copy from CommerceOnboarding) ────
function waitForGoogleMaps(): Promise<void> {
  return new Promise((resolve) => {
    if ((window as any).google?.maps?.places) { resolve(); return; }
    const interval = setInterval(() => {
      if ((window as any).google?.maps?.places) { clearInterval(interval); resolve(); }
    }, 100);
  });
}

// ── Design tokens ────────────────────────────────────────────────
const T = {
  bg:      "#FFFFFF",
  bgSoft:  "#F8F8FC",
  bgMuted: "#F0F0F6",
  dark:    "#0A0A0F",
  text:    "#1A1A2E",
  muted:   "#8080A0",
  dim:     "#B0B0C4",
  border:  "#E4E4EF",
  blue:    "#007BFF",
  red:     "#FF3D00",
  orange:  "#FF6B00",
  gold:    "#FFB800",
  white:   "#FFFFFF",
};
const GRAD      = `linear-gradient(90deg,${T.blue} 0%,${T.red} 40%,${T.orange} 70%,${T.gold} 100%)`;
const GRAD_SIZE = "300% 100%";

// ── Modes & steps ────────────────────────────────────────────────
type TopMode = "business" | "product" | "marketing";
type SubMode = "individual" | "bulk";

// Business individual: Identity(1) → Visuals(2) → Location(3) → Review(4)
// Business bulk:       Entries(1) → Confirm(2)
// Product individual:  Details(1) → Photos(2) → Contact(3)
// Product bulk:        Entries(1) → Confirm(2)
// Marketing:           Compose(1) → Confirm(2)
const BIZ_IND_STEPS  = ["Identity", "Visuals", "Location", "Review"];
const BIZ_BULK_STEPS = ["Entries",  "Confirm"];
const PROD_IND_STEPS = ["Details",  "Photos",   "Contact"];
const PROD_BULK_STEPS= ["Entries",  "Confirm"];
const MKT_STEPS      = ["Compose",  "Confirm"];

// ── Address type (matches CommerceOnboarding exactly) ────────────
interface AddressState {
  street:       string;
  city:         string;
  country:      string;
  county:       string;
  neighborhood: string;
  latitude:     number | undefined;
  longitude:    number | undefined;
}
const EMPTY_ADDRESS: AddressState = {
  street:"", city:"", country:"", county:"", neighborhood:"",
  latitude: undefined, longitude: undefined,
};

// ── Bulk row ─────────────────────────────────────────────────────
interface BulkEntry { id:string; name:string; email:string; city:string; country:string; price?:string; phone?:string; }
const newEntry = (): BulkEntry => ({ id:Math.random().toString(36).slice(2), name:"", email:"", city:"", country:"", price:"", phone:"" });

const MAX_IMAGES = 5;

// ── CSS ──────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'DM Sans',sans-serif;background:#fff;color:${T.dark};}
  @keyframes gradFlow{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes spin{to{transform:rotate(360deg);}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);}}
  .grad-text{background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 5s ease infinite;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
  .grad-bg{background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 4s ease infinite;}
  .acq-input{width:100%;padding:12px 16px;background:${T.white};border:1.5px solid ${T.border};border-radius:12px;color:${T.dark};font-size:14px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .2s,box-shadow .2s;}
  .acq-input:focus{border-color:${T.orange};box-shadow:0 0 0 3px rgba(255,107,0,.1);}
  .acq-input::placeholder{color:#bbb;}
  .acq-input[readonly]{background:${T.bgMuted};cursor:not-allowed;opacity:.7;}
  .acq-select{width:100%;padding:12px 16px;background:${T.white};border:1.5px solid ${T.border};border-radius:12px;color:${T.dark};font-size:14px;font-family:'DM Sans',sans-serif;outline:none;cursor:pointer;appearance:none;transition:border-color .2s;}
  .acq-select:focus{border-color:${T.orange};}
  .acq-select:disabled{background:${T.bgMuted};opacity:.55;cursor:not-allowed;}
  .acq-textarea{width:100%;padding:12px 16px;min-height:90px;resize:vertical;background:${T.white};border:1.5px solid ${T.border};border-radius:12px;color:${T.dark};font-size:14px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .2s;}
  .acq-textarea:focus{border-color:${T.orange};box-shadow:0 0 0 3px rgba(255,107,0,.1);}
  .btn-primary{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;border-radius:40px;border:none;background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 4s ease infinite;color:#fff;font-family:'Sora',sans-serif;font-size:14px;font-weight:700;cursor:pointer;transition:transform .15s,box-shadow .2s;white-space:nowrap;}
  .btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(255,107,0,.35);}
  .btn-primary:disabled{opacity:.4;cursor:not-allowed;transform:none;}
  .btn-ghost{display:inline-flex;align-items:center;gap:8px;padding:12px 24px;border-radius:40px;background:${T.bgSoft};border:1.5px solid ${T.border};color:${T.muted};font-family:'Sora',sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:background .2s,color .2s;}
  .btn-ghost:hover{background:${T.border};color:${T.dark};}
  .btn-icon{display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:8px;border:1.5px solid ${T.border};background:${T.white};cursor:pointer;transition:background .2s,border-color .2s;color:${T.muted};flex-shrink:0;}
  .btn-icon:hover{background:#fff0f0;border-color:#ffcccc;color:#e84020;}
  .mode-card{border:1.5px solid ${T.border};border-radius:18px;padding:22px 20px;cursor:pointer;transition:all .2s;background:${T.white};}
  .mode-card:hover{border-color:${T.orange};box-shadow:0 4px 20px rgba(255,107,0,.1);}
  .mode-card.active{border-color:transparent;background:${T.bgSoft};box-shadow:0 0 0 2px ${T.orange},0 8px 24px rgba(255,107,0,.12);}
  .sub-mode-btn{flex:1;padding:12px 0;border-radius:12px;border:1.5px solid ${T.border};background:${T.white};color:${T.muted};font-family:'Sora',sans-serif;font-size:12px;font-weight:700;cursor:pointer;transition:all .2s;letter-spacing:.06em;}
  .sub-mode-btn.active{background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 4s ease infinite;border-color:transparent;color:#fff;box-shadow:0 4px 16px rgba(255,107,0,.25);}
  .drop-zone{border:2px dashed ${T.border};border-radius:16px;padding:32px 20px;text-align:center;cursor:pointer;transition:all .2s;background:${T.bgSoft};}
  .drop-zone:hover,.drop-zone.drag{border-color:${T.orange};background:rgba(255,107,0,.04);}
  .photo-thumb{position:relative;aspect-ratio:1;border-radius:12px;overflow:hidden;border:1.5px solid ${T.border};}
  .photo-thumb img{width:100%;height:100%;object-fit:cover;display:block;}
  .photo-remove{position:absolute;top:5px;right:5px;width:22px;height:22px;border-radius:50%;background:rgba(0,0,0,.55);border:none;color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:12px;}
  .bulk-row{display:grid;gap:8px;align-items:start;padding:14px;border-radius:14px;background:${T.bgSoft};border:1.5px solid ${T.border};margin-bottom:10px;animation:fadeUp .2s ease;}
  .info-box{background:#FFF9F0;border:1px solid #FFE0B2;border-radius:12px;padding:14px 18px;font-size:13px;color:#7a5000;line-height:1.7;margin-bottom:20px;}
  .logo-upload-label{width:120px;height:120px;border:1.5px dashed ${T.border};border-radius:12px;background:${T.bgMuted};cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;overflow:hidden;position:relative;transition:border-color .2s;}
  .logo-upload-label:hover{border-color:${T.orange};}
  .review-card{border:1.5px solid ${T.border};border-radius:16px;overflow:hidden;margin-bottom:20px;background:${T.white};}
  .lbl{display:block;font-size:10px;font-weight:700;color:${T.orange};margin-bottom:7px;text-transform:uppercase;letter-spacing:.1em;font-family:'Sora',sans-serif;}
  .step-dot{height:6px;border-radius:999px;transition:all .3s;background:${T.border};}
  .step-dot.active{width:22px!important;background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 3s ease infinite;}
  .step-dot.done{background:${T.orange};}
  .err-box{background:#fff5f5;border:1px solid #ffcccc;border-radius:10px;padding:12px 16px;color:#c0392b;font-size:13px;margin-top:14px;}
  .overlay{position:fixed;inset:0;background:rgba(255,255,255,.92);backdrop-filter:blur(8px);z-index:9999;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:16px;}
  .sel-wrap{position:relative;}
  .sel-wrap .chev{position:absolute;right:12px;top:50%;transform:translateY(-50%);pointer-events:none;}
  /* Google Maps autocomplete dropdown */
  .pac-container{font-family:'DM Sans',sans-serif!important;font-size:13px!important;border:1.5px solid ${T.border}!important;border-top:none!important;box-shadow:0 8px 24px rgba(0,0,0,.08)!important;border-radius:0 0 12px 12px!important;z-index:99999!important;}
  .pac-item{padding:10px 14px!important;border-top:1px solid ${T.bgSoft}!important;color:${T.text}!important;cursor:pointer!important;line-height:1.5!important;}
  .pac-item:hover,.pac-item-selected{background:${T.bgSoft}!important;}
  .pac-item-query{font-size:13px!important;color:${T.dark}!important;font-weight:600!important;}
  .pac-icon{display:none!important;}
  .pac-matched{color:${T.orange}!important;font-weight:700!important;}
  @media(max-width:640px){.bulk-row{grid-template-columns:1fr 1fr!important;}.mode-grid{grid-template-columns:1fr!important;}.row2{grid-template-columns:1fr!important;}}
`;

// ════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ════════════════════════════════════════════════════════════════
const Acquisition: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const user     = useSelector((s: RootState) => s.auth.user);
  const userId   = user?.userId ?? "";

  // ── refs ──────────────────────────────────────────────────────
  const fileRef         = useRef<HTMLInputElement>(null);
  const logoRef         = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);

  // ── navigation state ──────────────────────────────────────────
  const [topMode, setTopMode] = useState<TopMode | null>(null);
  const [subMode, setSubMode] = useState<SubMode>("individual");
  const [step,    setStep]    = useState(0);   // 0 = top-mode picker

  // ── async state ───────────────────────────────────────────────
  const [busy,       setBusy]       = useState(false);
  const [overlayMsg, setOverlayMsg] = useState("");
  const [done,       setDone]       = useState(false);
  const [errMsg,     setErrMsg]     = useState("");
  const [drag,       setDrag]       = useState(false);
  const [mapsReady,  setMapsReady]  = useState(!!(window as any).google?.maps?.places);

  // ── BUSINESS fields ───────────────────────────────────────────
  const [bizName,     setBizName]     = useState("");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile,    setLogoFile]    = useState<File | null>(null);
  const [uploading,   setUploading]   = useState(false);
  const [bizAddress,  setBizAddress]  = useState<AddressState>(EMPTY_ADDRESS);
  const [ownerEmail,  setOwnerEmail]  = useState("");
  const [savedBizId,  setSavedBizId]  = useState<string | null>(null);

  // ── PRODUCT fields ────────────────────────────────────────────
  const [groupIndex,    setGroupIndex]    = useState<number | null>(null);
  const [categoryId,    setCategoryId]    = useState<number | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<number | null>(null);
  const [productTypeId, setProductTypeId] = useState<number | null>(null);
  const [productName,   setProductName]   = useState("");
  const [description,   setDescription]   = useState("");
  const [price,         setPrice]         = useState("");
  const [photos,        setPhotos]        = useState<File[]>([]);
  const [photoUrls,     setPhotoUrls]     = useState<string[]>([]);
  const [prodEmail,     setProdEmail]     = useState("");
  const [savedProdId,   setSavedProdId]   = useState("");

  // ── BULK fields ───────────────────────────────────────────────
  const [bulkEntries, setBulkEntries] = useState<BulkEntry[]>([newEntry(), newEntry()]);

  // ── MARKETING fields ──────────────────────────────────────────
  const [mktEmail,   setMktEmail]   = useState("");
  const [mktSubject, setMktSubject] = useState("");
  const [mktMessage, setMktMessage] = useState("");
  const [mktPhone,   setMktPhone]   = useState("");

  // ── PHONE fields (individual acquisition — optional WA) ────────
  const [ownerPhone, setOwnerPhone] = useState("");
  const [prodPhone,  setProdPhone]  = useState("");

  // ── derived category data ─────────────────────────────────────
  const group = groupIndex !== null ? CATEGORY_GROUPS[groupIndex] : null;
  const subs  = group ? (group.subs as any[]).filter((s: any) => s.parentId === categoryId) : [];
  const prods = group ? (group.products as any[]).filter((p: any) => p.parentId === subcategoryId) : [];

  // ── steps ─────────────────────────────────────────────────────
  const getSteps = () => {
    if (!topMode) return ["Mode"];
    if (topMode === "marketing") return MKT_STEPS;
    if (topMode === "business")  return subMode === "bulk" ? BIZ_BULK_STEPS : BIZ_IND_STEPS;
    return subMode === "bulk" ? PROD_BULK_STEPS : PROD_IND_STEPS;
  };
  const STEPS    = getSteps();
  const formStep = step - 1;
  const isFinal  = step > 0 && step === STEPS.length;

  // ── Wait for Google Maps ──────────────────────────────────────
  useEffect(() => {
    if (mapsReady) return;
    waitForGoogleMaps().then(() => setMapsReady(true));
  }, []);

  // ── Bind Google Maps Autocomplete on biz Location step ────────
  // Location is now step 3 (formStep === 2, step === 3)
  useEffect(() => {
    const isLocationStep = topMode === "business" && subMode === "individual" && step === 3;
    if (!isLocationStep || !mapsReady) return;

    const tid = setTimeout(() => {
      const input = addressInputRef.current;

      if (autocompleteRef.current) {
        (window as any).google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      if (!input) return;

      const autocomplete = new (window as any).google.maps.places.Autocomplete(input, {
        types: ["geocode"],
        fields: ["address_components", "geometry", "formatted_address"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;

        const comps = place.address_components as Array<{ types: string[]; long_name: string }>;
        const get   = (type: string) => comps.find(c => c.types.includes(type))?.long_name ?? "";

        const resolved: AddressState = {
          street:       `${get("street_number")} ${get("route")}`.trim(),
          city:         get("locality") || get("administrative_area_level_2"),
          neighborhood: get("sublocality") || get("neighborhood"),
          country:      get("country"),
          county:       get("administrative_area_level_1"),
          latitude:     place.geometry?.location?.lat(),
          longitude:    place.geometry?.location?.lng(),
        };

        setBizAddress(resolved);

        if (savedBizId) {
          dispatch(upsertCommercePartner({ brandId: savedBizId, data: { address: resolved } }) as any);
        }
      });

      autocompleteRef.current = autocomplete;
    }, 150);

    return () => clearTimeout(tid);
  }, [topMode, subMode, step, mapsReady, savedBizId]);

  // ── Clear autocomplete when leaving Location step ─────────────
  useEffect(() => {
    const isLocationStep = topMode === "business" && subMode === "individual" && step === 3;
    if (!isLocationStep && autocompleteRef.current) {
      (window as any).google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      autocompleteRef.current = null;
    }
  }, [step, topMode, subMode]);

  // ── helpers ───────────────────────────────────────────────────
  const addPhoto = (files: File[]) => {
    const v = files.filter(f => f.type.startsWith("image/")).slice(0, MAX_IMAGES - photos.length);
    setPhotos(p => [...p, ...v]);
    setPhotoUrls(p => [...p, ...v.map(f => URL.createObjectURL(f))]);
  };
  const removePhoto = (i: number) => {
    URL.revokeObjectURL(photoUrls[i]);
    setPhotos(p => p.filter((_,x) => x !== i));
    setPhotoUrls(p => p.filter((_,x) => x !== i));
  };
  const updateEntry = (id: string, patch: Partial<BulkEntry>) =>
    setBulkEntries(p => p.map(e => e.id === id ? { ...e, ...patch } : e));
  const removeEntry = (id: string) =>
    setBulkEntries(p => p.filter(e => e.id !== id));

  const uploadPhotosBase64 = async (): Promise<string[]> => {
    const out: string[] = [];
    for (const file of photos) {
      const b64 = await new Promise<string>(res => {
        const r = new FileReader();
        r.onload = () => res((r.result as string).split(",")[1]);
        r.readAsDataURL(file);
      });
      out.push(`data:${file.type};base64,${b64}`);
    }
    return out;
  };

  // ── validation ────────────────────────────────────────────────
  const isValid = (): boolean => {
    if (step === 0) return topMode !== null;

    if (topMode === "marketing") {
      if (formStep === 0) {
        const hasEmail = !!mktEmail.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mktEmail);
        const hasPhone = !!mktPhone.trim();
        return (hasEmail || hasPhone) && !!mktSubject.trim() && !!mktMessage.trim();
      }
      return true;
    }
    if (topMode === "business" && subMode === "bulk") {
      if (formStep === 0) return bulkEntries.length > 0 && bulkEntries.every(e => e.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.email));
      return true;
    }
    if (topMode === "business" && subMode === "individual") {
      if (formStep === 0) return !!bizName.trim();
      if (formStep === 1) return true;  // Visuals — optional
      if (formStep === 2) return true;  // Location — optional
      if (formStep === 3) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail);
    }
    if (topMode === "product" && subMode === "bulk") {
      if (formStep === 0) return bulkEntries.length > 0 && bulkEntries.every(e => e.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.email));
      return true;
    }
    if (topMode === "product" && subMode === "individual") {
      if (formStep === 0) return !!productName.trim() && groupIndex !== null && categoryId !== null;
      if (formStep === 1) return photos.length >= 1;
      if (formStep === 2) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(prodEmail);
    }
    return true;
  };

  // ── NEXT ──────────────────────────────────────────────────────
  const handleNext = async () => {
    if (!isValid()) return;
    setErrMsg("");
    setBusy(true); setOverlayMsg("Saving…");

    try {
      // BIZ: Identity → create draft partner
      if (topMode === "business" && subMode === "individual" && formStep === 0) {
        const r = await dispatch(upsertCommercePartner({
          brandId: savedBizId ?? undefined,
          data: { name: bizName, isDraft: true, isComplete: false, createdVia: "acquisition" },
        }) as any).unwrap();
        setSavedBizId(r.brandId ?? r.partnerId ?? null);
      }

      // BIZ: Visuals → upload logo
      if (topMode === "business" && subMode === "individual" && formStep === 1 && logoFile && savedBizId) {
        setOverlayMsg("Uploading logo…");
        setUploading(true);
        await dispatch(uploadCommercePartnerLogo({ brandId: savedBizId, file: logoFile }) as any).unwrap();
        setUploading(false);
      }

      // BIZ: Location is auto-saved on place_changed (via autocomplete listener)

      // PRODUCT: Details → create draft product
      if (topMode === "product" && subMode === "individual" && formStep === 0) {
        const r = await dispatch(upsertProduct({
          data: {
            name: productName, description,
            categoryId:    categoryId    ?? undefined,
            subcategoryId: subcategoryId ?? undefined,
            productId:     productTypeId ?? undefined,
            rate: parseFloat(price) || 0,
            isDraft: true, isPublished: false, createdVia: "acquisition",
          },
        })).unwrap();
        setSavedProdId(r.listingId ?? "");
      }

      // PRODUCT: Photos → upload
      if (topMode === "product" && subMode === "individual" && formStep === 1 && savedProdId && photos.length > 0) {
        setOverlayMsg("Uploading photos…");
        await dispatch(upsertProductMedia({ listingId: savedProdId, files: photos, type: "photo" })).unwrap();
      }

      setStep(s => s + 1);
    } catch (e: any) {
      setErrMsg(e.message || "Something went wrong");
    } finally {
      setBusy(false); setOverlayMsg(""); setUploading(false);
    }
  };

  // ── BACK ──────────────────────────────────────────────────────
  const handleBack = () => {
    setErrMsg("");
    if (step === 0) { navigate(-1); return; }
    if (step === 1) { setStep(0);   return; }
    setStep(s => s - 1);
  };

  // ── FINAL SUBMIT ──────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!isValid()) return;
    setBusy(true); setErrMsg("");

    try {
      // MARKETING
      if (topMode === "marketing") {
        const hasEmail = !!mktEmail.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mktEmail);
        const hasPhone = !!mktPhone.trim();

        if (hasEmail) {
          setOverlayMsg("Sending marketing email…");
          const res = await fetch(SEND_MARKETING_EMAIL_URL, {
            method:"POST", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({
              email: mktEmail, subject: mktSubject, message: mktMessage,
              sentBy: userId,
              phone: hasPhone ? mktPhone : undefined,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Failed");
        } else if (hasPhone) {
          setOverlayMsg("Sending WhatsApp message…");
          const res = await fetch(SEND_WA_MARKETING_URL, {
            method:"POST", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({ phone: mktPhone, subject: mktSubject, message: mktMessage, sentBy: userId }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Failed");
        }
        setDone(true); return;
      }

      // BULK
      if (subMode === "bulk") {
        setOverlayMsg(`Sending ${bulkEntries.length} messages…`);
        const res = await fetch(SEND_BULK_EMAIL_URL, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ entries: bulkEntries, type: topMode, submittedBy: userId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed");
        setDone(true); return;
      }

      // BUSINESS INDIVIDUAL
      if (topMode === "business" && subMode === "individual") {
        setOverlayMsg("Finalising…");
        if (savedBizId) {
          await dispatch(upsertCommercePartner({
            brandId: savedBizId,
            data: {
              email:     ownerEmail || undefined,
              phone:     ownerPhone || undefined,
              isClaimed: false,
              isDraft:   false,
              isComplete: true,
              address:   bizAddress.city ? bizAddress : undefined,
            },
          }) as any).unwrap();
        }
        setOverlayMsg("Sending…");
        const res = await fetch(SEND_INDIVIDUAL_EMAIL_URL, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({
            id: savedBizId, email: ownerEmail, mode: "individual_business",
            name: bizName, city: bizAddress.city, country: bizAddress.country,
            phone: ownerPhone || undefined,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed");
        setDone(true); return;
      }

      // PRODUCT INDIVIDUAL
      if (topMode === "product" && subMode === "individual") {
        setOverlayMsg("Finalising…");
        if (savedProdId) {
          await dispatch(upsertProduct({ listingId: savedProdId, data: { email: prodEmail || undefined, phone: prodPhone || undefined, isClaimed: false } })).unwrap();
        }
        setOverlayMsg("Sending…");
        const photoData = await uploadPhotosBase64();
        const res = await fetch(SEND_INDIVIDUAL_EMAIL_URL, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({
            id: savedProdId, email: prodEmail, mode: "individual_product",
            name: productName, description, price: parseFloat(price) || 0, photoData,
            phone: prodPhone || undefined,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed");
        setDone(true);
      }
    } catch (e: any) {
      setErrMsg(e.message || "Failed");
    } finally {
      setBusy(false); setOverlayMsg("");
    }
  };

  const resetAll = () => {
    setTopMode(null); setSubMode("individual"); setStep(0);
    setBizName(""); setLogoPreview(null); setLogoFile(null); setSavedBizId(null);
    setBizAddress(EMPTY_ADDRESS); setOwnerEmail(""); setOwnerPhone("");
    setGroupIndex(null); setCategoryId(null); setSubcategoryId(null); setProductTypeId(null);
    setProductName(""); setDescription(""); setPrice("");
    setPhotos([]); setPhotoUrls([]); setProdEmail(""); setProdPhone(""); setSavedProdId("");
    setBulkEntries([newEntry(), newEntry()]);
    setMktEmail(""); setMktSubject(""); setMktMessage(""); setMktPhone("");
    setDone(false); setErrMsg("");
  };

  const progress = STEPS.length > 1 && step > 0 ? ((step - 1) / (STEPS.length - 1)) * 100 : 0;

  // ════════════════════════════════════════════════════════════════
  //  SUCCESS
  // ════════════════════════════════════════════════════════════════
  if (done) return (
    <div style={{ minHeight:"100vh", background:T.white, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif" }}>
      <style>{CSS}</style>
      <div style={{ textAlign:"center", maxWidth:480, padding:40 }}>
        <div className="grad-bg" style={{ width:80, height:80, borderRadius:"50%", margin:"0 auto 24px", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <CheckCircle size={42} color="#fff"/>
        </div>
        <p style={{ fontFamily:"'Sora',sans-serif", fontSize:11, letterSpacing:"0.3em", marginBottom:10 }}>
          <span className="grad-text">{topMode === "marketing" ? (mktEmail && mktPhone ? "EMAIL & WHATSAPP SENT" : mktPhone ? "WHATSAPP SENT" : "MARKETING EMAIL SENT") : subMode === "bulk" ? "BULK ACQUISITION SENT" : "CLAIM EMAIL SENT"}</span>
        </p>
        <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:30, fontWeight:900, color:T.dark, marginBottom:12 }}>
          {subMode === "bulk" ? `${bulkEntries.length} emails sent! 🎉` : "Email sent! 🎉"}
        </h2>
        <p style={{ color:T.muted, fontSize:14, lineHeight:1.8, marginBottom:28 }}>
          {topMode === "marketing" ? `Marketing email sent to ${mktEmail}.`
            : subMode === "bulk" ? "Each recipient received a personalised claim email."
            : `Claim email sent to ${topMode === "business" ? ownerEmail : prodEmail}.`}
        </p>
        <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
          <button className="btn-ghost" onClick={resetAll}>Add Another</button>
          <button className="btn-primary" onClick={() => navigate("/dashboard")}>Dashboard <ArrowRight size={14}/></button>
        </div>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════════
  //  STEP CONTENT
  // ════════════════════════════════════════════════════════════════
  const renderContent = () => {

    // ── STEP 0: mode picker ──────────────────────────────────────
    if (step === 0) return (
      <div style={{ animation:"fadeUp .25s ease" }}>
        <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>What are you adding?</h2>
        <p style={{ color:T.muted, fontSize:14, marginBottom:28, lineHeight:1.6 }}>Onboard a business, list a product, or send a marketing email.</p>

        <div className="mode-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:24 }}>
          {([
            { key:"business",  Icon:Store,         label:"Business",  desc:"Onboard a seller store — mirrors full Commerce onboarding." },
            { key:"product",   Icon:Package,       label:"Product",   desc:"List a single product and contact the owner to claim it." },
            { key:"marketing", Icon:MessageSquare, label:"Marketing", desc:"Send a direct marketing email to a recipient." },
          ] as const).map(({ key, Icon, label, desc }) => (
            <div key={key} className={`mode-card${topMode === key ? " active" : ""}`} onClick={() => setTopMode(key)}>
              <div
                className={topMode === key ? "grad-bg" : ""}
                style={{ width:44, height:44, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10, ...(topMode !== key ? { background:T.bgMuted } : {}) }}>
                <Icon size={20} color={topMode === key ? "#fff" : T.muted}/>
              </div>
              <p style={{ fontFamily:"'Sora',sans-serif", fontWeight:700, fontSize:14, color:T.dark, marginBottom:6 }}>{label}</p>
              <p style={{ fontSize:12, color:T.muted, lineHeight:1.55 }}>{desc}</p>
            </div>
          ))}
        </div>

        {(topMode === "business" || topMode === "product") && (
          <div>
            <p style={{ fontSize:11, fontWeight:700, color:T.muted, letterSpacing:"0.12em", marginBottom:10, fontFamily:"'Sora',sans-serif" }}>SEND MODE</p>
            <div style={{ display:"flex", gap:10 }}>
              {(["individual","bulk"] as SubMode[]).map(m => (
                <button key={m} className={`sub-mode-btn${subMode === m ? " active" : ""}`} onClick={() => setSubMode(m)}>
                  {m === "individual" ? "Individual" : "Bulk"}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );

    const fs = formStep;

    // ════════════════════════════
    //  BUSINESS INDIVIDUAL
    // ════════════════════════════
    if (topMode === "business" && subMode === "individual") {

      // Step 1 — Identity
      if (fs === 0) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.28em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:12 }}>STEP 01 / 04</div>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Store Identity</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:24, lineHeight:1.6 }}>Choose a name that represents this commerce brand to customers.</p>
          <label className="lbl">Store Name *</label>
          <input className="acq-input" placeholder="e.g. Horizon Market" value={bizName} onChange={e => setBizName(e.target.value)}/>
        </div>
      );

      // Step 2 — Visuals
      if (fs === 1) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.28em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:12 }}>STEP 02 / 04</div>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Store Logo</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:24, lineHeight:1.6 }}>Upload the store logo. This is what customers see first.</p>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:14 }}>
            <label
              className="logo-upload-label"
              style={{ opacity: savedBizId ? 1 : 0.5, cursor: savedBizId ? "pointer" : "not-allowed" }}
              onClick={e => { if (!savedBizId) e.preventDefault(); }}>
              {logoPreview
                ? <img src={logoPreview} alt="logo" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                : <><Camera size={24} color={T.muted}/><span style={{ fontSize:9, color:T.muted, fontFamily:"'Sora',sans-serif", letterSpacing:"0.1em" }}>UPLOAD LOGO</span></>
              }
              {uploading && (
                <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(255,255,255,.7)" }}>
                  <div style={{ width:24, height:24, borderRadius:"50%", border:`3px solid ${T.dark}`, borderTopColor:"transparent", animation:"spin .8s linear infinite" }}/>
                </div>
              )}
              <input ref={logoRef} type="file" hidden accept="image/*" onChange={e => {
                const f = e.target.files?.[0];
                if (!f || !savedBizId) return;
                setLogoFile(f);
                setLogoPreview(URL.createObjectURL(f));
              }}/>
            </label>
            {!savedBizId
              ? <p style={{ fontSize:11, color:T.dim, fontFamily:"'Sora',sans-serif" }}>Save store name first to unlock logo upload</p>
              : (
                <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", background:"#FFF9F0", border:"1px solid #FFE0B2", borderRadius:10 }}>
                  <CheckCircle size={12} color={T.gold}/>
                  <span style={{ fontSize:11, color:"#7A5C00", fontFamily:"'Sora',sans-serif" }}>Store saved — you can now upload a logo</span>
                </div>
              )
            }
          </div>
        </div>
      );

      // Step 3 — Location
      if (fs === 2) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.28em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:12 }}>STEP 03 / 04</div>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Location</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:24, lineHeight:1.6 }}>Where is this store based? This helps match it with nearby customers.</p>

          {!mapsReady && (
            <div style={{ fontSize:11, color:T.gold, fontFamily:"'Sora',sans-serif", padding:"10px 14px", background:"#FFF9F0", border:`1px solid #FFE0B2`, borderRadius:10, marginBottom:14 }}>
              Loading address search…
            </div>
          )}

          <label className="lbl">Business Address</label>
          <div style={{ display:"flex", border:`1.5px solid ${T.border}`, borderRadius:12, background:T.white, marginBottom:14, overflow:"hidden" }}>
            <div style={{ padding:"0 13px", display:"flex", alignItems:"center", background:T.bgMuted, borderRight:`1px solid ${T.border}`, color:T.muted, flexShrink:0 }}>
              <MapPin size={14}/>
            </div>
            <input
              ref={addressInputRef}
              value={bizAddress.street}
              onChange={e => setBizAddress(a => ({ ...a, street: e.target.value }))}
              placeholder={mapsReady ? "Start typing your address…" : "Loading maps…"}
              autoComplete="off"
              style={{ flex:1, border:"none", outline:"none", padding:"12px 14px", fontSize:14, color:T.dark, background:"transparent", pointerEvents: mapsReady ? "auto" : "none", opacity: mapsReady ? 1 : 0.5, fontFamily:"'DM Sans',sans-serif" }}
            />
          </div>

          {bizAddress.city && (
            <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
              style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {[
                { label:"City",          val:bizAddress.city         },
                { label:"Country",       val:bizAddress.country      },
                { label:"Region",        val:bizAddress.county       },
                { label:"Neighbourhood", val:bizAddress.neighborhood },
              ].filter(f => f.val).map(f => (
                <div key={f.label}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.15em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:5 }}>{f.label.toUpperCase()}</div>
                  <div style={{ fontSize:13, color:T.dark, padding:"10px 14px", background:T.bgMuted, border:`1px solid ${T.border}`, borderRadius:10 }}>{f.val}</div>
                </div>
              ))}
              {bizAddress.latitude !== undefined && (
                <div style={{ gridColumn:"1/-1" }}>
                  <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.15em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:5 }}>COORDINATES</div>
                  <div style={{ fontSize:11, color:T.muted, padding:"8px 14px", background:T.bgMuted, border:`1px solid ${T.border}`, borderRadius:10, letterSpacing:"0.04em" }}>
                    {bizAddress.latitude?.toFixed(6)}, {bizAddress.longitude?.toFixed(6)}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      );

      // Step 4 — Review & Contact
      if (fs === 3) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.28em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:12 }}>STEP 04 / 04</div>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Review & Contact</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:24, lineHeight:1.6 }}>Enter the owner's email — we'll send a branded PDF with a one-click claim button.</p>

          <div className="review-card">
            <div style={{ height:72, background:T.bgMuted, position:"relative" }}>
              <div style={{ position:"absolute", bottom:-22, left:18, width:44, height:44, background:T.white, border:`2px solid ${T.white}`, overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,.12)", borderRadius:4 }}>
                {logoPreview
                  ? <img src={logoPreview} alt="logo" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                  : <div style={{ width:"100%", height:"100%", background:T.bgMuted, display:"flex", alignItems:"center", justifyContent:"center" }}><Store size={16} color={T.muted}/></div>
                }
              </div>
            </div>
            <div style={{ padding:"30px 18px 18px" }}>
              <p style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:18, color:T.dark, marginBottom:8 }}>{bizName}</p>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr" }}>
                {[
                  ["City",    bizAddress.city    || "—"],
                  ["Country", bizAddress.country || "—"],
                ].map(([k,v]) => (
                  <div key={k} style={{ padding:"8px 0", borderBottom:`1px solid ${T.border}` }}>
                    <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.14em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:2 }}>{k.toUpperCase()}</div>
                    <div style={{ fontSize:12, color:T.dark }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <label className="lbl">Owner Email *</label>
          <input className="acq-input" type="email" placeholder="owner@business.com" value={ownerEmail} onChange={e => setOwnerEmail(e.target.value)} style={{ marginBottom:12 }}/>

          <label className="lbl">WhatsApp Number <span style={{ color:T.muted, fontWeight:400, textTransform:"none", letterSpacing:0 }}>(optional)</span></label>
          <div style={{ display:"flex", border:`1.5px solid ${T.border}`, borderRadius:12, background:T.white, overflow:"hidden" }}>
            <div style={{ padding:"0 13px", display:"flex", alignItems:"center", background:T.bgMuted, borderRight:`1px solid ${T.border}`, color:T.muted, flexShrink:0 }}>
              <Phone size={14}/>
            </div>
            <input className="acq-input" style={{ border:"none", outline:"none", borderRadius:0 }} placeholder="+1 234 567 8900" value={ownerPhone} onChange={e => setOwnerPhone(e.target.value)}/>
          </div>

          {errMsg && <div className="err-box">⚠️ {errMsg}</div>}
        </div>
      );
    }

    // ════════════════════════════
    //  BUSINESS BULK
    // ════════════════════════════
    if (topMode === "business" && subMode === "bulk") {

      if (fs === 0) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Bulk Business Entries</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:20, lineHeight:1.6 }}>Add one row per business. Each will receive a personalised claim email.</p>
          {bulkEntries.map((entry) => (
            <div key={entry.id} className="bulk-row" style={{ gridTemplateColumns:"1fr 1fr 1fr 1fr auto" }}>
              <div>
                <label className="lbl">Name *</label>
                <input className="acq-input" placeholder="Business name" value={entry.name} onChange={e => updateEntry(entry.id, { name: e.target.value })}/>
              </div>
              <div>
                <label className="lbl">Email *</label>
                <input className="acq-input" type="email" placeholder="owner@shop.com" value={entry.email} onChange={e => updateEntry(entry.id, { email: e.target.value })}/>
              </div>
              <div>
                <label className="lbl">City</label>
                <input className="acq-input" placeholder="City" value={entry.city} onChange={e => updateEntry(entry.id, { city: e.target.value })}/>
              </div>
              <div>
                <label className="lbl">Country</label>
                <input className="acq-input" placeholder="Country" value={entry.country} onChange={e => updateEntry(entry.id, { country: e.target.value })}/>
              </div>
              <button className="btn-icon" style={{ marginTop:22 }} onClick={() => removeEntry(entry.id)}><Trash2 size={14}/></button>
            </div>
          ))}
          <button className="btn-ghost" onClick={() => setBulkEntries(p => [...p, newEntry()])} style={{ marginTop:8 }}>
            <Plus size={14}/> Add Row
          </button>
        </div>
      );

      if (fs === 1) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Confirm Bulk Send</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:20, lineHeight:1.6 }}>Review the {bulkEntries.length} entries before sending.</p>
          <div className="info-box">
            <strong>📧 {bulkEntries.length} emails</strong> will be sent. Each recipient gets a personalised claim email with their business details.
          </div>
          {bulkEntries.map((e, i) => (
            <div key={e.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderRadius:10, background:T.bgSoft, border:`1px solid ${T.border}`, marginBottom:8 }}>
              <div style={{ width:28, height:28, borderRadius:"50%", background:GRAD, backgroundSize:GRAD_SIZE, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:"#fff", flexShrink:0 }}>{i+1}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.dark, marginBottom:2 }}>{e.name}</p>
                <p style={{ fontSize:11, color:T.muted }}>{e.email}{e.city ? ` · ${e.city}` : ""}{e.country ? `, ${e.country}` : ""}</p>
              </div>
            </div>
          ))}
          {errMsg && <div className="err-box">⚠️ {errMsg}</div>}
        </div>
      );
    }

    // ════════════════════════════
    //  PRODUCT INDIVIDUAL
    // ════════════════════════════
    if (topMode === "product" && subMode === "individual") {

      // Step 1 — Details
      if (fs === 0) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.28em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:12 }}>STEP 01 / 03</div>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Product Details</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:24, lineHeight:1.6 }}>Enter the product info. Categories come from the same system as AddProducts.</p>

          <label className="lbl">Product Name *</label>
          <input className="acq-input" placeholder="e.g. Samsung Galaxy S25 Ultra" value={productName} onChange={e => setProductName(e.target.value)} style={{ marginBottom:14 }}/>

          <label className="lbl">Description</label>
          <textarea className="acq-textarea" placeholder="Describe this product…" value={description} onChange={e => setDescription(e.target.value)} style={{ marginBottom:14 }}/>

          <label className="lbl">Price (€)</label>
          <input className="acq-input" type="number" placeholder="0.00" value={price} onChange={e => setPrice(e.target.value)} style={{ marginBottom:20 }}/>

          {/* Category Group */}
          <label className="lbl">Category Group *</label>
          <div className="sel-wrap" style={{ marginBottom:12 }}>
            <select className="acq-select" value={groupIndex ?? ""} onChange={e => {
              const v = e.target.value === "" ? null : Number(e.target.value);
              setGroupIndex(v); setCategoryId(null); setSubcategoryId(null); setProductTypeId(null);
            }}>
              <option value="">Select group…</option>
              {CATEGORY_GROUPS.map((g, i) => <option key={i} value={i}>{g.name}</option>)}
            </select>
            <ChevronDown size={14} color={T.muted} className="chev"/>
          </div>

          {/* Category */}
          <label className="lbl">Category *</label>
          <div className="sel-wrap" style={{ marginBottom:12 }}>
            <select className="acq-select" value={categoryId ?? ""} disabled={!group}
              onChange={e => { const v = Number(e.target.value); setCategoryId(v); setSubcategoryId(null); setProductTypeId(null); }}>
              <option value="">Select category…</option>
              {(group?.categories ?? []).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <ChevronDown size={14} color={T.muted} className="chev"/>
          </div>

          {/* Subcategory */}
          {subs.length > 0 && (
            <>
              <label className="lbl">Subcategory</label>
              <div className="sel-wrap" style={{ marginBottom:12 }}>
                <select className="acq-select" value={subcategoryId ?? ""}
                  onChange={e => { const v = Number(e.target.value); setSubcategoryId(v); setProductTypeId(null); }}>
                  <option value="">Select subcategory…</option>
                  {subs.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <ChevronDown size={14} color={T.muted} className="chev"/>
              </div>
            </>
          )}

          {/* Product Type */}
          {prods.length > 0 && (
            <>
              <label className="lbl">Product Type</label>
              <div className="sel-wrap">
                <select className="acq-select" value={productTypeId ?? ""}
                  onChange={e => setProductTypeId(Number(e.target.value))}>
                  <option value="">Select product type…</option>
                  {prods.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <ChevronDown size={14} color={T.muted} className="chev"/>
              </div>
            </>
          )}
        </div>
      );

      // Step 2 — Photos
      if (fs === 1) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.28em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:12 }}>STEP 02 / 03</div>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Product Photos</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:24, lineHeight:1.6 }}>Add up to {MAX_IMAGES} photos — they appear in the claim email.</p>
          <div
            className={`drop-zone${drag ? " drag" : ""}`}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); addPhoto(Array.from(e.dataTransfer.files)); }}>
            <Camera size={28} color={T.muted} style={{ margin:"0 auto 10px" }}/>
            <p style={{ fontFamily:"'Sora',sans-serif", fontWeight:700, color:T.dark, marginBottom:4 }}>{drag ? "Drop here" : "Click or drag photos"}</p>
            <p style={{ fontSize:12, color:T.muted }}>{photoUrls.length}/{MAX_IMAGES} · JPG, PNG, WEBP</p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={e => addPhoto(Array.from(e.target.files || []))}/>
          {photoUrls.length > 0 && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginTop:14 }}>
              {photoUrls.map((url, i) => (
                <div key={i} className="photo-thumb">
                  <img src={url} alt=""/>
                  {i === 0 && <div style={{ position:"absolute", bottom:5, left:5, background:T.orange, borderRadius:4, padding:"2px 7px", fontSize:9, fontWeight:700, color:"#fff" }}>Cover</div>}
                  <button className="photo-remove" onClick={() => removePhoto(i)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      );

      // Step 3 — Contact
      if (fs === 2) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.28em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:12 }}>STEP 03 / 03</div>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Contact Owner</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:24, lineHeight:1.6 }}>We'll send a branded PDF with a one-click claim button.</p>

          <div className="info-box">
            <strong>📧 What happens:</strong> The owner receives a preview of their product listing and a <strong>Claim Product</strong> button. One click creates their account — completely free.
          </div>

          {/* Summary */}
          <div style={{ border:`1.5px solid ${T.border}`, borderRadius:14, overflow:"hidden", marginBottom:20, background:T.white }}>
            {photoUrls[0] && <img src={photoUrls[0]} alt="" style={{ width:"100%", height:160, objectFit:"cover", display:"block" }}/>}
            <div style={{ padding:"14px 16px" }}>
              <p style={{ fontFamily:"'Sora',sans-serif", fontWeight:700, fontSize:15, color:T.dark, marginBottom:4 }}>{productName}</p>
              {price && <p style={{ fontFamily:"'Sora',sans-serif", fontWeight:700, fontSize:14, color:T.orange }}>€{price}</p>}
              {group && <p style={{ fontSize:11, color:T.muted, marginTop:4 }}>{group.name}{categoryId ? ` · ${(group.categories as any[]).find((c: any) => c.id === categoryId)?.name ?? ""}` : ""}</p>}
            </div>
          </div>

          <label className="lbl">Owner Email *</label>
          <input className="acq-input" type="email" placeholder="owner@product.com" value={prodEmail} onChange={e => setProdEmail(e.target.value)} style={{ marginBottom:12 }}/>

          <label className="lbl">WhatsApp Number <span style={{ color:T.muted, fontWeight:400, textTransform:"none", letterSpacing:0 }}>(optional)</span></label>
          <div style={{ display:"flex", border:`1.5px solid ${T.border}`, borderRadius:12, background:T.white, overflow:"hidden" }}>
            <div style={{ padding:"0 13px", display:"flex", alignItems:"center", background:T.bgMuted, borderRight:`1px solid ${T.border}`, color:T.muted, flexShrink:0 }}>
              <Phone size={14}/>
            </div>
            <input className="acq-input" style={{ border:"none", outline:"none", borderRadius:0 }} placeholder="+1 234 567 8900" value={prodPhone} onChange={e => setProdPhone(e.target.value)}/>
          </div>

          {errMsg && <div className="err-box">⚠️ {errMsg}</div>}
        </div>
      );
    }

    // ════════════════════════════
    //  PRODUCT BULK
    // ════════════════════════════
    if (topMode === "product" && subMode === "bulk") {

      if (fs === 0) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Bulk Product Entries</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:20, lineHeight:1.6 }}>Add one row per product owner.</p>
          {bulkEntries.map((entry) => (
            <div key={entry.id} className="bulk-row" style={{ gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr auto" }}>
              <div>
                <label className="lbl">Product *</label>
                <input className="acq-input" placeholder="Product name" value={entry.name} onChange={e => updateEntry(entry.id, { name: e.target.value })}/>
              </div>
              <div>
                <label className="lbl">Email *</label>
                <input className="acq-input" type="email" placeholder="owner@email.com" value={entry.email} onChange={e => updateEntry(entry.id, { email: e.target.value })}/>
              </div>
              <div>
                <label className="lbl">Price</label>
                <input className="acq-input" type="number" placeholder="0.00" value={entry.price} onChange={e => updateEntry(entry.id, { price: e.target.value })}/>
              </div>
              <div>
                <label className="lbl">City</label>
                <input className="acq-input" placeholder="City" value={entry.city} onChange={e => updateEntry(entry.id, { city: e.target.value })}/>
              </div>
              <div>
                <label className="lbl">Country</label>
                <input className="acq-input" placeholder="Country" value={entry.country} onChange={e => updateEntry(entry.id, { country: e.target.value })}/>
              </div>
              <button className="btn-icon" style={{ marginTop:22 }} onClick={() => removeEntry(entry.id)}><Trash2 size={14}/></button>
            </div>
          ))}
          <button className="btn-ghost" onClick={() => setBulkEntries(p => [...p, newEntry()])} style={{ marginTop:8 }}>
            <Plus size={14}/> Add Row
          </button>
        </div>
      );

      if (fs === 1) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Confirm Bulk Send</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:20, lineHeight:1.6 }}>Review the {bulkEntries.length} entries before sending.</p>
          <div className="info-box">
            <strong>📦 {bulkEntries.length} product emails</strong> will be sent. Each owner gets a personalised claim email.
          </div>
          {bulkEntries.map((e, i) => (
            <div key={e.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderRadius:10, background:T.bgSoft, border:`1px solid ${T.border}`, marginBottom:8 }}>
              <div style={{ width:28, height:28, borderRadius:"50%", background:GRAD, backgroundSize:GRAD_SIZE, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:"#fff", flexShrink:0 }}>{i+1}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.dark, marginBottom:2 }}>{e.name}</p>
                <p style={{ fontSize:11, color:T.muted }}>{e.email}{e.price ? ` · €${e.price}` : ""}{e.city ? ` · ${e.city}` : ""}</p>
              </div>
            </div>
          ))}
          {errMsg && <div className="err-box">⚠️ {errMsg}</div>}
        </div>
      );
    }

    // ════════════════════════════
    //  MARKETING
    // ════════════════════════════
    if (topMode === "marketing") {

      if (fs === 0) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Compose Message</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:24, lineHeight:1.6 }}>Provide at least one of email or WhatsApp number.</p>

          <label className="lbl">Recipient Email</label>
          <input className="acq-input" type="email" placeholder="recipient@company.com" value={mktEmail} onChange={e => setMktEmail(e.target.value)} style={{ marginBottom:12 }}/>

          <label className="lbl">WhatsApp Number <span style={{ color:T.muted, fontWeight:400, textTransform:"none", letterSpacing:0 }}>(optional)</span></label>
          <div style={{ display:"flex", border:`1.5px solid ${T.border}`, borderRadius:12, background:T.white, overflow:"hidden", marginBottom:16 }}>
            <div style={{ padding:"0 13px", display:"flex", alignItems:"center", background:T.bgMuted, borderRight:`1px solid ${T.border}`, color:T.muted, flexShrink:0 }}>
              <Phone size={14}/>
            </div>
            <input className="acq-input" style={{ border:"none", outline:"none", borderRadius:0 }} placeholder="+1 234 567 8900" value={mktPhone} onChange={e => setMktPhone(e.target.value)}/>
          </div>

          <label className="lbl">Subject *</label>
          <input className="acq-input" placeholder="e.g. Join our platform — free listing" value={mktSubject} onChange={e => setMktSubject(e.target.value)} style={{ marginBottom:12 }}/>

          <label className="lbl">Message *</label>
          <textarea className="acq-textarea" placeholder="Write your marketing message…" value={mktMessage} onChange={e => setMktMessage(e.target.value)}/>
        </div>
      );

      if (fs === 1) return (
        <div style={{ animation:"fadeUp .25s ease" }}>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:26, fontWeight:900, color:T.dark, marginBottom:6 }}>Confirm & Send</h2>
          <p style={{ color:T.muted, fontSize:14, marginBottom:24, lineHeight:1.6 }}>Review your message before sending.</p>
          <div style={{ border:`1.5px solid ${T.border}`, borderRadius:14, overflow:"hidden", background:T.white, marginBottom:20 }}>
            <div style={{ background:T.bgSoft, padding:"12px 16px", borderBottom:`1px solid ${T.border}` }}>
              <p style={{ fontSize:11, fontWeight:700, color:T.muted, fontFamily:"'Sora',sans-serif", letterSpacing:"0.1em" }}>TO</p>
              <p style={{ fontSize:13, color:T.dark, marginTop:3 }}>{mktEmail || "—"}</p>
              {mktPhone && <p style={{ fontSize:12, color:T.muted, marginTop:2 }}>WhatsApp: {mktPhone}</p>}
            </div>
            <div style={{ padding:"14px 16px" }}>
              <p style={{ fontSize:11, fontWeight:700, color:T.muted, fontFamily:"'Sora',sans-serif", letterSpacing:"0.1em", marginBottom:4 }}>SUBJECT</p>
              <p style={{ fontSize:14, fontWeight:700, color:T.dark, marginBottom:12 }}>{mktSubject}</p>
              <p style={{ fontSize:11, fontWeight:700, color:T.muted, fontFamily:"'Sora',sans-serif", letterSpacing:"0.1em", marginBottom:4 }}>MESSAGE</p>
              <p style={{ fontSize:13, color:T.text, lineHeight:1.7, whiteSpace:"pre-wrap" }}>{mktMessage}</p>
            </div>
          </div>
          {errMsg && <div className="err-box">⚠️ {errMsg}</div>}
        </div>
      );
    }

    return null;
  };

  // ════════════════════════════════════════════════════════════════
  //  SHELL
  // ════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif", paddingBottom:90 }}>
      <style>{CSS}</style>

      {/* Overlay */}
      {busy && (
        <div className="overlay">
          <div style={{ width:48, height:48, border:`3px solid rgba(255,107,0,.2)`, borderTop:`3px solid ${T.orange}`, borderRadius:"50%", animation:"spin .8s linear infinite" }}/>
          <p style={{ color:T.muted, fontSize:13, fontFamily:"'Sora',sans-serif", letterSpacing:"0.12em" }}>{overlayMsg || "SAVING…"}</p>
        </div>
      )}

      {/* Header */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:"rgba(255,255,255,.95)", backdropFilter:"blur(12px)", borderBottom:`1px solid ${T.border}` }}>
        <div style={{ height:3, background:GRAD, backgroundSize:GRAD_SIZE, animation:"gradFlow 4s ease infinite" }}/>
        <div style={{ maxWidth:680, margin:"0 auto", padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <button onClick={handleBack} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:T.muted, fontFamily:"'Sora',sans-serif", fontSize:13, fontWeight:600 }}>
            <ArrowLeft size={16}/> {step === 0 ? "Cancel" : "Back"}
          </button>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            {step > 0 && STEPS.map((_, i) => (
              <div key={i} className={`step-dot${i === formStep ? " active" : i < formStep ? " done" : ""}`} style={{ width: i === formStep ? 22 : 8 }}/>
            ))}
          </div>
          <div style={{ width:60 }}/>
        </div>
        {step > 0 && (
          <div style={{ height:2, background:T.bgMuted, maxWidth:680, margin:"0 auto" }}>
            <div style={{ height:"100%", background:GRAD, backgroundSize:GRAD_SIZE, animation:"gradFlow 4s ease infinite", width:`${progress}%`, transition:"width .35s ease" }}/>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ maxWidth:680, margin:"0 auto", padding:"32px 20px" }}>
        {renderContent()}
      </div>

      {/* Footer nav */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"rgba(255,255,255,.96)", backdropFilter:"blur(12px)", borderTop:`1px solid ${T.border}`, padding:"14px 24px", display:"flex", justifyContent:"flex-end", gap:12, zIndex:100 }}>
        {isFinal ? (
          <button className="btn-primary" disabled={!isValid() || busy} onClick={handleSubmit}>
            <Send size={14}/>
            {topMode === "marketing" ? "Send Message" : subMode === "bulk" ? `Send ${bulkEntries.length} Emails` : "Send Claim Email"}
          </button>
        ) : (
          <button className="btn-primary" disabled={!isValid() || busy} onClick={handleNext}>
            Next <ArrowRight size={14}/>
          </button>
        )}
      </div>
    </div>
  );
};

export default Acquisition;